/**
 * CallWindow — Phase 2 Task 7 + Phase 3 Track A (screen-share).
 *
 * Self-contained in-call dialog. Reads all state from `useCallController()`
 * (provided by the app-wide `<CallProvider>`) and joins media via `useCall`.
 *
 * States:
 *   active === null              → render nothing (closed Dialog)
 *   active.state === "ringing"   → "Ringing {peerName}…" + Cancel
 *   active.state === "connecting"|"connected" → video + Hang up (+ Share screen when supported)
 *   active.state === "ended"     → a brief reason-worded blurb, e.g. "Call ended" /
 *                                  "Call failed — couldn't connect" (`callEndedLabel`,
 *                                  overridable per-call via `active.note`; the
 *                                  provider clears it after ~1.5s)
 *
 * This file owns the call wiring — the `useCall` media join, the stream→<video>
 * binding effects, the ring/connect/end lifecycle, and the broker handlers. The
 * presentational pieces live under `call-window/`: `VideoStage` (the meters +
 * video + PiPs), `ControlFooter` (the control bar), `DeviceMenu` (the device
 * picker), `callStatus` (the header status mapping), and `useDraggableWindow`
 * (the floating-widget drag).
 *
 * Joins the media room exactly once per room allocation (guarded by a ref
 * so React Strict Mode double-effects don't double-join).
 *
 * See docs/calls.md for the three-plane architecture.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { Box, Button, IconButton, Paper, Snackbar, Typography } from '@mui/material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MinimizeRoundedIcon from '@mui/icons-material/MinimizeRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';

import { callEndedLabel } from '@xop/dashboard-shared';
import { useCallController } from './CallProvider.js';
import { useCall, canShareScreen } from './useCall.js';
import { dialCall, streamerControls, fetchCallAttendees } from './api.js';
import { backgroundFxSupported, type BackgroundMode } from './media/backgroundFx.js';
import {
  applyAudioOutput,
  loadDevicePref,
  saveDevicePref,
  speakerSelectionSupported,
} from './media/devicePrefs.js';
import { DialPad } from './call-window/DialPad.js';
import { AddPeopleMenu } from './call-window/AddPeopleMenu.js';
import { useChatOverview } from '../lib/chat/hooks.js';
import { useCallTone } from './useCallTone.js';
import { useSessionUser } from '../auth/AuthProvider.js';
import { callStatus } from './call-window/callStatus.js';
import { ControlFooter } from './call-window/ControlFooter.js';
import { DeviceMenu } from './call-window/DeviceMenu.js';
import { useDraggableWindow } from './call-window/useDraggableWindow.js';
import { VideoStage } from './call-window/VideoStage.js';
import { ParticipantGrid } from './call-window/ParticipantGrid.js';
import { ScreenShareStage } from './call-window/ScreenShareStage.js';

/** Attach a stream to a <video>, tolerating environments that reject srcObject
 * (a fake stream in tests, or a webview without full MediaStream support). */
function setSrcObject(el: HTMLVideoElement | null, stream: MediaStream | null): void {
  if (!el || !stream) return;
  try {
    el.srcObject = stream;
    // WKWebView (macOS desktop) does not reliably autoplay a MediaStream from
    // the `autoPlay` attribute alone — call play() explicitly. Safe on Chromium
    // (a redundant play() on an already-playing element is a no-op).
    const p = el.play?.();
    if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay/not-ready — ignore */ });
  } catch {
    /* unsupported env / invalid stream — ignore */
  }
}

/** Which device row the menu should check: the pinned choice, or — when nothing
 *  is pinned (we follow the OS default) — the list's `'default'` entry (Chromium
 *  exposes one) or its first device, so the default is visibly selected. */
function effectiveDeviceId(selected: string | null, devices: MediaDeviceInfo[]): string | null {
  if (selected) return selected;
  return devices.find((d) => d.deviceId === 'default')?.deviceId ?? devices[0]?.deviceId ?? null;
}

export function CallWindow(): JSX.Element | null {
  const { active, screen, camera, roster, invite, cancel, hangup, markConnected, signalScreenShare, signalCamera, reportSid, consumePendingNumbers } = useCallController();
  const user = useSessionUser();
  const myName = user.name;
  // A real group call = more than two participants on the roster. Drives the
  // people-grid (vs the 1:1 VideoStage) and the group header title.
  const isGroup = roster.length > 2;
  // Header label: a group shows its headcount ("Group · 5") so the size is
  // obvious even in the title bar; a 1:1 shows the peer's name.
  const headerTitle = isGroup ? `Group · ${roster.length}` : (active?.peerName ?? 'Call');

  // Staff directory for the "Add person" picker (cached; shared with /chat).
  const overview = useChatOverview(active !== null);
  const contacts = overview.data?.contacts ?? [];

  // Guard: only join once per roomId to avoid double-joins in React Strict Mode.
  const joinedRoomIdRef = useRef<number | null>(null);
  // Guard: drain the launcher's auto-dial queue (active.pendingNumbers) exactly
  // once per call — tracks the callId we've already dialled for.
  const pendingDialedRef = useRef<string | null>(null);

  const onConnected = useCallback(() => {
    markConnected();
  }, [markConnected]);

  // When the base call drops at the MEDIA layer without a local hangup (the C1
  // server terminated the conference, or the peer's media died), drive the
  // broker hangup so the window closes + a call record posts on this side too.
  const onMediaClosed = useCallback(() => {
    void hangup();
  }, [hangup]);

  // Report our SFU sid to the server on join → it lands in the roster so peers
  // can subscribe to our camera (group all-to-all video).
  const onSid = useCallback((sid: number) => {
    void reportSid(sid);
  }, [reportSid]);

  const {
    connectionState,
    remoteStream,
    localAudioStream,
    muted,
    localScreenStream,
    remoteScreenStream,
    isSharing,
    cameraOn,
    localCameraStream,
    remoteCameraStream,
    remoteCameras,
    backgroundMode,
    backgroundBusy,
    webcastToken,
    joinRoom,
    joinConsultation,
    hangup: mediaHangup,
    toggleMute,
    switchAudioInput,
    switchVideoInput,
    setBackgroundMode,
    startScreenShare,
    stopScreenShare,
    viewScreen,
    stopViewScreen,
    enableCamera,
    disableCamera,
    syncCameraViews,
  } = useCall(useMemo(() => ({ onConnected, onMediaClosed, onSid }), [onConnected, onMediaClosed, onSid]));

  // remoteRef = a hidden sink that PLAYS the peer's audio (the base call is
  // audio-only). remoteCameraRef = the peer's visible camera (a separate SFU
  // session). remoteScreenRef = the peer's screen. localCameraRef = self-view.
  const remoteRef = useRef<HTMLVideoElement>(null);
  const remoteScreenRef = useRef<HTMLVideoElement>(null);
  const remoteCameraRef = useRef<HTMLVideoElement>(null);
  const localCameraRef = useRef<HTMLVideoElement>(null);
  const localScreenRef = useRef<HTMLVideoElement>(null);

  // Track the sessionId used when screen-share / camera started (for stop signal)
  const sharingSessionIdRef = useRef<number | null>(null);
  const cameraSessionIdRef = useRef<number | null>(null);

  // Inline "not available" message state
  const [screenUnavailableOpen, setScreenUnavailableOpen] = useState(false);
  // Shown when a background effect was requested but couldn't initialise.
  const [bgUnavailableOpen, setBgUnavailableOpen] = useState(false);
  // Keypad (dial an external phone into the call) + its busy/result state.
  const [keypadAnchor, setKeypadAnchor] = useState<HTMLElement | null>(null);
  const [dialBusy, setDialBusy] = useState(false);
  const [dialMessage, setDialMessage] = useState<string | null>(null);
  // "Add person" picker anchor.
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);

  const handleAddPerson = useCallback(
    (p: { user_id: number; display_name: string }) => {
      setAddAnchor(null);
      void invite({ user_id: p.user_id, name: p.display_name })
        .then(() => setDialMessage(`Ringing ${p.display_name}…`))
        .catch(() => setDialMessage(`Couldn't add ${p.display_name}.`));
    },
    [invite],
  );

  const handleDial = useCallback(
    (number: string) => {
      const callId = active?.callId;
      if (!callId || !webcastToken) {
        setDialMessage("Can't dial yet — the call isn't fully connected.");
        return;
      }
      setDialBusy(true);
      void dialCall(callId, number, webcastToken)
        .then((r) => {
          // DIAGNOSTIC (2026-06-25): C1's `status:true` is only a daemon ACK, not a
          // ring. Log its raw reply so an ACK-but-no-ring dial is visible in devtools.
          // eslint-disable-next-line no-console
          console.info('[xop-call] dial_out C1 reply', { number, c1: r?.c1 });
          setDialMessage(`Calling ${number}…`);
          setKeypadAnchor(null);
        })
        .catch(() => setDialMessage(`Couldn't call ${number}.`))
        .finally(() => setDialBusy(false));
    },
    [active?.callId, webcastToken],
  );
  // Auto-dial the launcher's queued PSTN numbers via dial_out once the call is
  // connected and the webcast token exists (a phone-only call starts already
  // `connecting` with a solo room; a staff call gets here after the first
  // accept). The FIRST number of a fresh call is normally placed as a
  // consultation dial (see the join effect), so it is NOT dial_out'd again —
  // UNLESS we fell back to a room join (no moderator pin), in which case it
  // joins the pending list here. Fires once per call via pendingDialedRef.
  useEffect(() => {
    const callId = active?.callId;
    if (!callId || !webcastToken) return;
    const usedConsultation = Boolean(active?.consultationNumber && active?.room?.moderatorPin);
    const nums = (
      usedConsultation
        ? active?.pendingNumbers ?? []
        : [active?.consultationNumber, ...(active?.pendingNumbers ?? [])]
    ).filter((n): n is string => Boolean(n));
    if (nums.length === 0) return;
    if (pendingDialedRef.current === callId) return;
    pendingDialedRef.current = callId;
    void (async () => {
      for (const n of nums) {
        try {
          await dialCall(callId, n, webcastToken);
          setDialMessage(`Calling ${n}…`);
        } catch {
          setDialMessage(`Couldn't call ${n}.`);
        }
      }
    })();
    consumePendingNumbers();
  }, [active?.callId, active?.pendingNumbers, active?.consultationNumber, active?.room?.moderatorPin, webcastToken, consumePendingNumbers]);

  // Best-effort capability gate for showing the Background picker at all.
  const bgSupported = backgroundFxSupported();

  const handlePickBackground = useCallback(
    (mode: BackgroundMode) => {
      // Keep the menu open so the user can audition effects; surface failures.
      // The .catch() guarantees a rejected switch (e.g. a replaceTrack error)
      // can never become an unhandled rejection — it degrades to the toast.
      void setBackgroundMode(mode)
        .then((result) => {
          if (result === 'unsupported') setBgUnavailableOpen(true);
        })
        .catch(() => setBgUnavailableOpen(true));
    },
    [setBackgroundMode],
  );
  // Window size: minimized (a small Messenger-style pill) · floating (the small
  // widget) · maximized (fills the app's inner content). `minimized` is
  // orthogonal to `maximized` so restoring returns to the prior size. Reset per call.
  const [maximized, setMaximized] = useState(false);
  const [minimized, setMinimized] = useState(false);
  // Sids that C1's get_attendees reports as currently publishing camera — the
  // authoritative discovery feed for catching a late joiner up (see effect below).
  const [discoveredCameraSids, setDiscoveredCameraSids] = useState<number[]>([]);

  // Device-settings menu (pick mic / speaker / camera). The selected ids are
  // seeded from the remembered choice (localStorage) so the menu shows a check on
  // the device the user pinned; `null` = follow the OS default (resolved to the
  // default row for the check via `effectiveDeviceId`). See media/devicePrefs.ts.
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioId, setSelectedAudioId] = useState<string | null>(() => loadDevicePref('audioinput'));
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(() => loadDevicePref('videoinput'));
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState<string | null>(() =>
    loadDevicePref('audiooutput'),
  );
  // Speaker (audio-output) selection only exists where the platform supports
  // HTMLMediaElement.setSinkId (Chromium / Windows-desktop / Firefox — NOT the
  // macOS-desktop WKWebView, where the speaker always follows the OS default).
  const speakerSupported = useMemo(() => speakerSelectionSupported(), []);

  const openSettings = useCallback((e: ReactMouseEvent<HTMLElement>) => {
    setSettingsAnchor(e.currentTarget);
    void navigator.mediaDevices?.enumerateDevices?.().then((devices) => {
      setAudioInputs(devices.filter((d) => d.kind === 'audioinput'));
      setVideoInputs(devices.filter((d) => d.kind === 'videoinput'));
      setAudioOutputs(devices.filter((d) => d.kind === 'audiooutput'));
    }).catch(() => { /* enumeration unavailable */ });
  }, []);

  const hasScreenShare = remoteScreenStream !== null;
  const hasPeerCamera = remoteCameraStream !== null;
  // The peer SIGNALLED they turned on camera/screen (WS frame arrived) but the
  // media stream hasn't started flowing yet → show a "connecting" indicator so
  // it's clear something is loading (esp. the first connect). Also a useful
  // diagnostic: if this shows, the signal reached us and the view leg is up.
  const peerVideoConnecting =
    (camera.sharingSessionIds.length > 0 && remoteCameraStream === null) ||
    (screen.remoteSharing && remoteScreenStream === null);
  // Auto-enlarge ONLY for the party VIEWING the peer's screen — they need the
  // room to read it. The SHARER stays in the small floating widget so the call
  // window doesn't cover the screen they're presenting.
  const screenActive = hasScreenShare;

  // Bind the base call's remote stream to the hidden audio sink (peer voice).
  // Also re-bind on a state change: a consultation dial sets `remoteStream` while
  // still `ringing` (the audio sink isn't mounted yet — only the `inCall` branch
  // renders it), so we must re-bind when it mounts on the flip to `connected`.
  useEffect(() => {
    setSrcObject(remoteRef.current, remoteStream);
  }, [remoteStream, active?.state]);

  // Route the peer's voice to the remembered speaker (or the OS default when
  // unset). Re-applies whenever the sink element (re)mounts with a stream or the
  // chosen output changes. No-op where setSinkId is unsupported (macOS desktop).
  useEffect(() => {
    void applyAudioOutput(remoteRef.current, selectedAudioOutputId);
  }, [selectedAudioOutputId, remoteStream, active?.state]);

  // Bind the screen remote stream to its video element whenever it changes.
  useEffect(() => {
    setSrcObject(remoteScreenRef.current, remoteScreenStream);
  }, [remoteScreenStream]);

  // Bind the peer's camera stream (re-runs when the element swaps primary↔PiP).
  useEffect(() => {
    setSrcObject(remoteCameraRef.current, remoteCameraStream);
  }, [remoteCameraStream, hasScreenShare]);

  // Bind the local camera self-view. (The <video> mounts whenever
  // localCameraStream is set, so this same dep both mounts + binds — fixing the
  // black self-preview where the element mounted on a later render than the bind.)
  useEffect(() => {
    setSrcObject(localCameraRef.current, localCameraStream);
  }, [localCameraStream]);

  // Bind the local screen self-preview (what I'm sharing).
  useEffect(() => {
    setSrcObject(localScreenRef.current, localScreenStream);
  }, [localScreenStream]);

  // Join the media room exactly once when a room becomes available. A FRESH
  // outbound PSTN call (active.consultationNumber + a moderator routing context)
  // places an H&H-style consultation dial — a DIRECT JsSIP INVITE to the number —
  // instead of a normal room join + dial_out. Without the moderator pin (older
  // server) it falls back to joinRoom, and the drain effect below dial_out's the
  // number.
  useEffect(() => {
    if (!active?.room) return;
    if (joinedRoomIdRef.current === active.room.roomId) return;
    joinedRoomIdRef.current = active.room.roomId;
    if (active.consultationNumber && active.room.moderatorPin) {
      void joinConsultation(active.room, active.consultationNumber, myName);
    } else {
      void joinRoom(active.room, myName);
    }
  }, [active?.room, active?.consultationNumber, joinRoom, joinConsultation, myName]);

  // ── Draggable floating window ───────────────────────────────────────────────
  // Drag is disabled only when actually showing the maximized full-screen view
  // (the minimized pill IS draggable, even if the maximized flag is still set).
  const { winRef, pos, resetPos, onDragStart, onDragMove, onDragEnd } = useDraggableWindow(maximized && !minimized);

  // Reset the join guard + the window size whenever the active call clears, so
  // the next call starts as the small floating widget.
  useEffect(() => {
    if (!active) {
      joinedRoomIdRef.current = null;
      pendingDialedRef.current = null;
      setMaximized(false);
      setMinimized(false);
      setDiscoveredCameraSids([]);
      resetPos();
    }
  }, [active, resetPos]);

  // React to peer screen-share state from the provider.
  useEffect(() => {
    if (screen.remoteSharing && screen.remoteSharerSessionId !== null) {
      void viewScreen(screen.remoteSharerSessionId);
    } else {
      stopViewScreen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.remoteSharing, screen.remoteSharerSessionId]);

  // All-to-all camera subscription: reconcile our remote-camera view legs to the
  // set of peers currently publishing (every `call_camera{sharing:true}` sid).
  // Subsumes the 1:1 path (a 2-person call has exactly one desired sid) and
  // scales to N — each new sharer adds a leg, each stop/leave drops one.
  // viewCamera() self-guards, so our own sid is never subscribed.
  // Also re-run once media auth is ready (webcastToken set): a camera frame —
  // especially the server's replay-to-late-joiner — can land BEFORE joinRoom's
  // auth resolves, when viewCamera would no-op; this picks up the backlog then.
  //
  // The DESIRED set unions two sources: (a) fast broker `call_camera` frames
  // (camera.sharingSessionIds) and (b) C1's AUTHORITATIVE get_attendees snapshot
  // (discoveredCameraSids) — the latter is what catches a LATE joiner up on
  // cameras already live, independent of broker frame timing.
  useEffect(() => {
    const desired = [...new Set([...camera.sharingSessionIds, ...discoveredCameraSids])];
    syncCameraViews(desired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.sharingSessionIds, discoveredCameraSids, webcastToken]);

  // Discovery: whenever membership changes (roster), media auth becomes ready, OR
  // the base call CONNECTS, ask C1 who is ALREADY publishing camera
  // (get_attendees → camera_started) and feed those sids into the desired set
  // above. This is the robust late-joiner path the broker frames alone miss — and
  // re-fetching on `active.state` ('connecting'→'connected') is WS-INDEPENDENT, so
  // a late joiner still catches up even if the realtime socket (which delivers
  // call_roster/call_camera) is flapping. Best-effort (failure → keep frame-driven).
  useEffect(() => {
    const callId = active?.callId;
    if (!callId || !webcastToken) return;
    let cancelled = false;
    void fetchCallAttendees(callId, webcastToken)
      .then((r) => {
        if (cancelled) return;
        setDiscoveredCameraSids(r.attendees.filter((a) => a.camera_started).map((a) => a.id));
      })
      .catch(() => { /* best-effort — frame-driven subscription still runs */ });
    return () => { cancelled = true; };
  }, [active?.callId, active?.state, webcastToken, roster]);

  // When the call ends from the OTHER side (peer hung up / ring timed out /
  // server terminated — the provider sets active.state='ended' off the broker
  // call_state frame), tear down OUR media immediately so no camera/mic/screen
  // keeps capturing and no SFU session lingers. mediaHangup() is idempotent.
  useEffect(() => {
    if (active?.state === 'ended') mediaHangup();
  }, [active?.state, mediaHangup]);

  // Caller-side ringback ("ring-ring") while waiting for the other side to pick
  // up. The receiver hears the melodic ringtone (IncomingCallModal) instead. For
  // a consultation dial this plays until the dialed party goes off-hook (the
  // answer-poll below flips `active.state` ringing→connected).
  useCallTone(active?.state === 'ringing', 'ringback');

  // Auto-size: a screen share (mine or theirs) needs room → expand to the big
  // window; when sharing ends, drop back to the small floating widget. Only
  // fires on the transition, so the manual resize button still works in between.
  const prevScreenActiveRef = useRef(false);
  useEffect(() => {
    if (screenActive !== prevScreenActiveRef.current) {
      prevScreenActiveRef.current = screenActive;
      setMaximized(screenActive);
    }
  }, [screenActive]);

  const handleHangup = useCallback(() => {
    mediaHangup();
    void hangup();
  }, [mediaHangup, hangup]);

  // ── Consultation answer detection (off-hook monitoring) ───────────────────
  // A fresh outbound dial connects OUR leg to C1 immediately (C1 answers our
  // INVITE to bridge), BEFORE the dialed party picks up. So we don't join the
  // conference instantly — we keep ringing (local ringback) and POLL the
  // conference's voice ports until the dialed PSTN leg is off-hook
  // (`isPhone && audioConnected`), then markConnected (stops ringback, shows the
  // in-call UI). Safe degrades: if the conference returns no port data at all we
  // connect after a short grace (so a call never hangs); a hard ring timeout ends
  // an unanswered call. (See docs/calls.md → consultation dial-out.)
  useEffect(() => {
    const callId = active?.callId;
    const isConsultationRinging = Boolean(
      active?.consultationNumber && active?.room?.moderatorPin && active?.state === 'ringing',
    );
    if (!callId || !webcastToken || !isConsultationRinging) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let polls = 0;
    let sawPorts = false;
    const startedAt = Date.now();
    const RING_TIMEOUT_MS = 45_000;
    const NO_PORTS_GRACE_POLLS = 4; // ~6s with no port data at all → connect anyway
    const POLL_MS = 1500;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      polls += 1;
      try {
        const r = await fetchCallAttendees(callId, webcastToken);
        if (cancelled) return;
        const ports = r.audioPorts ?? [];
        if (ports.length > 0) sawPorts = true;
        const answered = ports.some((p) => p.isPhone && p.audioConnected);
        // Diagnostic for live tuning of the off-hook signal.
        // eslint-disable-next-line no-console
        console.info('[xop-call] consultation answer-poll', { polls, answered, ports });
        if (answered) { markConnected(); return; }
        // Degrade: conference never reported voice ports → can't detect off-hook
        // here, so connect rather than ring forever.
        if (!sawPorts && polls >= NO_PORTS_GRACE_POLLS) {
          // eslint-disable-next-line no-console
          console.warn('[xop-call] consultation: no conference port data — connecting without off-hook detection');
          markConnected();
          return;
        }
        if (Date.now() - startedAt > RING_TIMEOUT_MS) {
          // eslint-disable-next-line no-console
          console.info('[xop-call] consultation: ring timeout — no answer');
          handleHangup();
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      if (!cancelled) timer = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [active?.callId, active?.consultationNumber, active?.room?.moderatorPin, active?.state, webcastToken, markConnected, handleHangup]);

  // Register/unregister a published stream with C1's SFU so it FORWARDS to
  // viewers (a raw publish leg is never routed until streamer_controls fires).
  const registerStream = useCallback(
    (source: 'camera' | 'screen', state: 'start_sharing' | 'stop_sharing', sid: number, mediaCount = 0) => {
      const callId = active?.callId;
      if (!callId || !webcastToken || sid <= 0) return;
      void streamerControls(callId, source, state, sid, webcastToken, mediaCount).catch(() => {
        /* best-effort — the broker frame still drives the UI; surfaced via logs */
      });
    },
    [active?.callId, webcastToken],
  );

  const handleShareScreen = useCallback(async () => {
    if (isSharing) {
      // Stop sharing — always signal the broker even if the ref is stale
      stopScreenShare();
      const sid = sharingSessionIdRef.current ?? 0;
      void signalScreenShare(false, sid);
      registerStream('screen', 'stop_sharing', sid);
      sharingSessionIdRef.current = null;
      return;
    }

    const sid = await startScreenShare();
    if (sid === null) {
      // getDisplayMedia was rejected (NotAllowedError, or unsupported webview)
      setScreenUnavailableOpen(true);
      return;
    }

    sharingSessionIdRef.current = sid;
    void signalScreenShare(true, sid);
    registerStream('screen', 'start_sharing', sid, 1);
  }, [isSharing, startScreenShare, stopScreenShare, signalScreenShare, registerStream]);

  const handleToggleCamera = useCallback(async () => {
    if (cameraOn) {
      const sid = cameraSessionIdRef.current ?? 0;
      disableCamera();
      void signalCamera(false, sid);
      registerStream('camera', 'stop_sharing', sid);
      cameraSessionIdRef.current = null;
      return;
    }
    const sid = await enableCamera();
    if (sid === null) {
      setScreenUnavailableOpen(true); // reuse the inline "not available" toast
      return;
    }
    cameraSessionIdRef.current = sid;
    void signalCamera(true, sid);
    registerStream('camera', 'start_sharing', sid);
  }, [cameraOn, enableCamera, disableCamera, signalCamera, registerStream]);

  const isOpen = active !== null;
  const isRinging = active?.state === 'ringing';
  const isEnded = active?.state === 'ended';
  const inCall = active?.state === 'connecting' || active?.state === 'connected';
  const isConnected = active?.state === 'connected';

  // Connection-health indicator for the header (label + dot colour).
  const { label: statusLabel, color: statusColor } = callStatus(active?.state, connectionState);

  const showShareButton = isConnected && canShareScreen();
  const showCameraButton = isConnected;

  // Self-preview PiP size — a bit bigger in the expanded window.
  const pipW = maximized ? 180 : 88;
  const pipH = maximized ? 101 : 50;

  return (
    <>
      {isOpen && (
        <Paper
          ref={winRef}
          elevation={8}
          data-testid="call-window"
          data-call-state={active?.state ?? 'idle'}
          data-call-size={minimized ? 'minimized' : maximized ? 'maximized' : 'floating'}
          sx={{
            position: 'fixed',
            zIndex: (t) => t.zIndex.modal + 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'common.black',
            boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
            ...(minimized
              ? {
                  // Messenger-style pill, parked bottom-right (or wherever dragged).
                  ...(pos ? { top: pos.y, left: pos.x } : { bottom: 96, right: 88 }),
                  width: 'auto',
                  borderRadius: 8,
                }
              : maximized
                ? {
                    // Fill the app's inner content area (below the 56px top nav),
                    // edge to edge — full-bleed.
                    top: 56,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: 0,
                  }
                : {
                    // Default anchor clears the Otto chat launcher in the
                    // bottom-right corner (bigger bottom + right margin).
                    ...(pos ? { top: pos.y, left: pos.x } : { bottom: 96, right: 88 }),
                    // Wide enough that the full control row (7 round buttons) sits
                    // comfortably on one line; the footer also wraps as a safety net.
                    width: 360,
                    borderRadius: 2,
                  }),
          }}
        >
          {minimized ? (
            /* Minimized pill (Messenger-style): drag to move; expand or hang up. */
            <Box
              data-testid="call-window-drag-handle"
              onPointerDown={onDragStart}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                pl: 1.5,
                pr: 0.5,
                py: 0.5,
                cursor: 'move',
                userSelect: 'none',
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor, flexShrink: 0 }} />
              <Typography noWrap sx={{ maxWidth: 130, color: 'common.white', fontSize: 13, fontWeight: 600 }}>
                {headerTitle}
              </Typography>
              <IconButton
                data-testid="call-window-expand"
                size="small"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => setMinimized(false)}
                aria-label="Expand call"
                sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'common.white' } }}
              >
                <OpenInFullRoundedIcon fontSize="small" />
              </IconButton>
              <IconButton
                data-testid="call-window-bubble-hangup"
                size="small"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={handleHangup}
                aria-label="Hang up"
                sx={{ bgcolor: 'error.main', color: 'common.white', '&:hover': { bgcolor: 'error.dark' } }}
              >
                <CallEndIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            /* Drag handle header — move the window without blocking the app */
            <Box
              data-testid="call-window-drag-handle"
              onPointerDown={onDragStart}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 1,
                cursor: maximized ? 'default' : 'move',
                userSelect: 'none',
                bgcolor: 'rgba(255,255,255,0.06)',
              }}
            >
              <DragIndicatorIcon
                sx={{ fontSize: 18, color: 'rgba(255,255,255,0.45)', visibility: maximized ? 'hidden' : 'visible' }}
              />
              <Typography noWrap sx={{ flex: 1, minWidth: 0, color: 'common.white', fontSize: 14, fontWeight: 600 }}>
                {headerTitle}
              </Typography>
              <Box
                data-testid="call-status"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor, flexShrink: 0 }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{statusLabel}</Typography>
              </Box>
              {inCall && (
                <IconButton
                  data-testid="call-window-minimize"
                  size="small"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => setMinimized(true)}
                  aria-label="Minimize call"
                  sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'common.white' } }}
                >
                  <MinimizeRoundedIcon fontSize="small" />
                </IconButton>
              )}
              {inCall && (
                <IconButton
                  data-testid="call-window-resize"
                  size="small"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => setMaximized((m) => !m)}
                  aria-label={maximized ? 'Shrink to floating window' : 'Expand call'}
                  sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'common.white' } }}
                >
                  {maximized ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
              )}
            </Box>
          )}

          {isRinging && !minimized && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                gap: 2,
              }}
            >
              <Typography sx={{ color: 'common.white', fontSize: 15 }}>
                Ringing {active?.peerName ?? ''}…
              </Typography>
              <Button
                color="error"
                variant="contained"
                startIcon={<CallEndIcon />}
                onClick={() => (active?.consultationNumber ? handleHangup() : void cancel())}
                aria-label="Cancel call"
                sx={{ borderRadius: 6, px: 3 }}
              >
                Cancel
              </Button>
            </Box>
          )}

          {inCall && (
            <Box
              sx={{
                display: minimized ? 'none' : 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              {/* Hidden audio sink — the base call is audio-only; this plays the
                  peer's voice regardless of which video is on screen. Kept MOUNTED
                  even when minimized so audio + stream bindings survive. */}
              <video ref={remoteRef} autoPlay playsInline style={{ display: 'none' }} />

              {/* Group (>2 participants): a screen share takes the stage (the grid
                  has no place to render it → viewers saw black), otherwise a
                  responsive people grid. 1:1 keeps the VideoStage (it handles both). */}
              {isGroup ? (
                (remoteScreenStream ?? (isSharing ? localScreenStream : null)) ? (
                  <ScreenShareStage
                    screenStream={(remoteScreenStream ?? localScreenStream)!}
                    ownScreen={remoteScreenStream === null}
                    localCameraStream={localCameraStream}
                    maximized={maximized}
                    pipW={pipW}
                    pipH={pipH}
                  />
                ) : (
                  <ParticipantGrid
                    participants={roster}
                    selfId={user.id}
                    maximized={maximized}
                    muted={muted}
                    remoteCameras={remoteCameras}
                    localCameraStream={localCameraStream}
                  />
                )
              ) : (
                <VideoStage
                  maximized={maximized}
                  peerName={active?.peerName}
                  muted={muted}
                  localAudioStream={localAudioStream}
                  remoteStream={remoteStream}
                  hasScreenShare={hasScreenShare}
                  hasPeerCamera={hasPeerCamera}
                  peerVideoConnecting={peerVideoConnecting}
                  localCameraStream={localCameraStream}
                  localScreenStream={localScreenStream}
                  pipW={pipW}
                  pipH={pipH}
                  remoteScreenRef={remoteScreenRef}
                  remoteCameraRef={remoteCameraRef}
                  localCameraRef={localCameraRef}
                  localScreenRef={localScreenRef}
                />
              )}

              <ControlFooter
                muted={muted}
                onToggleMute={toggleMute}
                showCameraButton={showCameraButton}
                cameraOn={cameraOn}
                onToggleCamera={() => void handleToggleCamera()}
                showShareButton={showShareButton}
                isSharing={isSharing}
                onShareScreen={() => void handleShareScreen()}
                showAddButton={isConnected}
                onOpenAddPeople={(e) => setAddAnchor(e.currentTarget)}
                showKeypadButton={isConnected}
                onOpenKeypad={(e) => setKeypadAnchor(e.currentTarget)}
                onOpenSettings={openSettings}
                onHangup={handleHangup}
                maximized={maximized}
              />

              <DialPad
                anchorEl={keypadAnchor}
                onClose={() => setKeypadAnchor(null)}
                onDial={handleDial}
                busy={dialBusy}
              />

              <AddPeopleMenu
                anchorEl={addAnchor}
                onClose={() => setAddAnchor(null)}
                people={contacts.map((c) => ({ user_id: c.user_id, display_name: c.display_name }))}
                excludeIds={roster.map((p) => p.user_id)}
                onPick={handleAddPerson}
              />

              <DeviceMenu
                anchorEl={settingsAnchor}
                onClose={() => setSettingsAnchor(null)}
                audioInputs={audioInputs}
                videoInputs={videoInputs}
                audioOutputs={audioOutputs}
                selectedAudioId={effectiveDeviceId(selectedAudioId, audioInputs)}
                selectedVideoId={effectiveDeviceId(selectedVideoId, videoInputs)}
                selectedAudioOutputId={effectiveDeviceId(selectedAudioOutputId, audioOutputs)}
                onPickAudio={(id) => {
                  setSelectedAudioId(id);
                  void switchAudioInput(id);
                  setSettingsAnchor(null);
                }}
                onPickVideo={(id) => {
                  setSelectedVideoId(id);
                  void switchVideoInput(id);
                  setSettingsAnchor(null);
                }}
                onPickAudioOutput={(id) => {
                  setSelectedAudioOutputId(id);
                  saveDevicePref('audiooutput', id);
                  void applyAudioOutput(remoteRef.current, id);
                  setSettingsAnchor(null);
                }}
                showSpeakers={speakerSupported}
                showBackground={bgSupported}
                backgroundMode={backgroundMode}
                backgroundBusy={backgroundBusy}
                onPickBackground={handlePickBackground}
              />
            </Box>
          )}

          {isEnded && !minimized && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
              <Typography sx={{ color: 'common.white', fontSize: 15 }}>
                {/* A custom note wins; otherwise the reason picks the wording, so
                    a failed setup reads as a failure and not as a plain hang-up. */}
                {active?.note ?? callEndedLabel(active?.reason)}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Graceful fallback when getDisplayMedia is rejected */}
      <Snackbar
        open={screenUnavailableOpen}
        autoHideDuration={4000}
        onClose={() => setScreenUnavailableOpen(false)}
        message="Screen sharing isn't available here yet"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Graceful fallback when a background effect can't initialise */}
      <Snackbar
        open={bgUnavailableOpen}
        autoHideDuration={4000}
        onClose={() => setBgUnavailableOpen(false)}
        message="Background effects aren't available on this device — using your camera as-is"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Dial result (calling… / couldn't call) */}
      <Snackbar
        open={dialMessage !== null}
        autoHideDuration={4000}
        onClose={() => setDialMessage(null)}
        message={dialMessage ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
